'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TestimonialCarouselProps {
  currentLanguage: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  location: string;
  image: string;
  alt: string;
  quote: string;
  impact: string;
}

const TestimonialCarousel = ({ currentLanguage }: TestimonialCarouselProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const content = {
    en: {
      title: 'Client Impact Stories',
      subtitle: 'Real-world sustainability transformations across industries and regions',
      testimonials: [
      {
        name: '',
        role: 'Manufacturing Sector',
        company: 'Automotive Component Manufacturer',
        location: 'Mumbai, India',
        image: '',
        alt: '',
        quote: 'Implemented comprehensive carbon accounting system across 12 production facilities. Achieved ISO 14064 certification and established baseline for net-zero roadmap.',
        impact: '45% reduction in Scope 1 & 2 emissions'
      },
      {
        name: '',
        role: 'Real Estate Development',
        company: 'Commercial Property Portfolio',
        location: 'Dubai, UAE',
        image: '',
        alt: '',
        quote: 'Deployed water conservation and energy efficiency programs across 2.5 million sq ft of commercial space. Integrated IoT monitoring for real-time resource tracking.',
        impact: '2.3M liters water saved annually, 30% energy reduction'
      },
      {
        name: '',
        role: 'Agriculture & Farming',
        company: 'Regenerative Agriculture Cooperative',
        location: 'Punjab, India',
        image: '',
        alt: '',
        quote: 'Established carbon credit generation program for 450+ smallholder farmers. Implemented soil health monitoring and sustainable farming practices with multilingual training.',
        impact: '₹8.5 lakhs additional annual revenue per farm, 12,000 tCO2e sequestered'
      }]

    },
    hi: {
      title: 'ग्राहक प्रभाव कहानियां',
      subtitle: 'उद्योगों और क्षेत्रों में वास्तविक स्थिरता परिवर्तन',
      testimonials: [
      {
        name: '',
        role: 'विनिर्माण क्षेत्र',
        company: 'ऑटोमोटिव कंपोनेंट निर्माता',
        location: 'मुंबई, भारत',
        image: '',
        alt: '',
        quote: '12 उत्पादन सुविधाओं में व्यापक कार्बन लेखा प्रणाली लागू की। ISO 14064 प्रमाणन प्राप्त किया और नेट-जीरो रोडमैप के लिए आधार रेखा स्थापित की।',
        impact: 'स्कोप 1 और 2 उत्सर्जन में 45% कमी'
      },
      {
        name: '',
        role: 'रियल एस्टेट विकास',
        company: 'वाणिज्यिक संपत्ति पोर्टफोलियो',
        location: 'दुबई, UAE',
        image: '',
        alt: '',
        quote: '2.5 मिलियन वर्ग फुट वाणिज्यिक स्थान में जल संरक्षण और ऊर्जा दक्षता कार्यक्रम तैनात किए। वास्तविक समय संसाधन ट्रैकिंग के लिए IoT निगरानी एकीकृत की।',
        impact: 'सालाना 2.3M लीटर पानी की बचत, 30% ऊर्जा में कमी'
      },
      {
        name: '',
        role: 'कृषि और खेती',
        company: 'पुनर्योजी कृषि सहकारी',
        location: 'पंजाब, भारत',
        image: '',
        alt: '',
        quote: '450+ छोटे किसानों के लिए कार्बन क्रेडिट उत्पादन कार्यक्रम स्थापित किया। बहुभाषी प्रशिक्षण के साथ मिट्टी स्वास्थ्य निगरानी और टिकाऊ खेती प्रथाओं को लागू किया।',
        impact: '₹8.5 लाख अतिरिक्त वार्षिक राजस्व प्रति फार्म, 12,000 tCO2e संग्रहीत'
      }]

    },
    ar: {
      title: 'قصص تأثير العملاء',
      subtitle: 'تحولات الاستدامة الواقعية عبر الصناعات والمناطق',
      testimonials: [
      {
        name: '',
        role: 'قطاع التصنيع',
        company: 'مصنع مكونات السيارات',
        location: 'مومباي، الهند',
        image: '',
        alt: '',
        quote: 'تنفيذ نظام محاسبة الكربون الشامل عبر 12 منشأة إنتاج. حصل على شهادة ISO 14064 وأنشأ خط أساس لخارطة طريق صافي الصفر.',
        impact: 'انخفاض بنسبة 45٪ في انبعاثات النطاق 1 و 2'
      },
      {
        name: '',
        role: 'تطوير العقارات',
        company: 'محفظة العقارات التجارية',
        location: 'دبي، الإمارات',
        image: '',
        alt: '',
        quote: 'نشر برامج الحفاظ على المياه وكفاءة الطاقة عبر 2.5 مليون قدم مربع من المساحات التجارية. دمج مراقبة إنترنت الأشياء لتتبع الموارد في الوقت الفعلي.',
        impact: 'توفير 2.3 مليون لتر من المياه سنوياً، تخفيض 30٪ في الطاقة'
      },
      {
        name: '',
        role: 'الزراعة والفلاحة',
        company: 'تعاونية الزراعة التجديدية',
        location: 'البنجاب، الهند',
        image: '',
        alt: '',
        quote: 'إنشاء برنامج توليد أرصدة الكربون لأكثر من 450 مزارع صغير. تنفيذ مراقبة صحة التربة وممارسات الزراعة المستدامة مع التدريب متعدد اللغات.',
        impact: '8.5 لكه روبية إيرادات سنوية إضافية لكل مزرعة، 12,000 طن مكافئ ثاني أكسيد الكربون'
      }]

    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % currentContent.testimonials.length);
  };

  const handlePrev = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev - 1 + currentContent.testimonials.length) % currentContent.testimonials.length);
  };

  const currentTestimonial = currentContent.testimonials[currentIndex];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="relative">
          <div className="bg-card rounded-2xl border border-border shadow-natural-lg overflow-hidden">
            <div className="grid md:grid-cols-5 gap-8 p-8 md:p-12">
              {/* Image Section - Hidden */}
              <div className="md:col-span-2 flex flex-col items-center space-y-4" style={{display: 'none'}}>
                <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-primary shadow-natural">
                  <AppImage
                    src={currentTestimonial.image || '/assets/images/no_image.png'}
                    alt={currentTestimonial.alt || 'Client testimonial'}
                    className="w-full h-full object-cover" />

                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-heading font-bold text-xl text-text-primary">
                    {currentTestimonial.name}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {currentTestimonial.role}
                  </p>
                  <p className="text-sm font-semibold text-primary">
                    {currentTestimonial.company}
                  </p>
                  <div className="flex items-center justify-center space-x-1 text-xs text-text-secondary">
                    <Icon name="MapPinIcon" size={14} />
                    <span>{currentTestimonial.location}</span>
                  </div>
                </div>
              </div>

              {/* Content Section - Full Width */}
              <div className="md:col-span-5 flex flex-col justify-center space-y-6">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="MapPinIcon" size={16} className="text-primary" />
                    <span className="font-semibold">{currentTestimonial.location}</span>
                    <span className="mx-2">•</span>
                    <span>{currentTestimonial.role}</span>
                  </div>
                  <p className="text-xs text-text-secondary uppercase tracking-wide">
                    {currentTestimonial.company}
                  </p>
                </div>
                
                <div className="relative">
                  <Icon
                    name="ChatBubbleLeftIcon"
                    size={48}
                    className="text-primary/20 absolute -top-4 -left-2" />

                  <p className="text-lg text-text-primary leading-relaxed relative z-10 pl-8">
                    {currentTestimonial.quote}
                  </p>
                </div>

                <div className="flex items-center space-x-3 bg-surface rounded-lg p-4">
                  <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="ChartBarIcon" size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary uppercase tracking-wide">Impact Achieved</p>
                    <p className="font-heading font-bold text-lg text-text-primary">
                      {currentTestimonial.impact}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <button
              onClick={handlePrev}
              disabled={!isHydrated}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-surface hover:border-primary transition-all duration-200 disabled:opacity-50"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeftIcon" size={24} className="text-text-primary" />
            </button>

            <div className="flex space-x-2">
              {currentContent.testimonials.map((_, index) =>
              <button
                key={index}
                onClick={() => isHydrated && setCurrentIndex(index)}
                disabled={!isHydrated}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === index ? 'bg-primary w-8' : 'bg-border'}`
                }
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>

            <button
              onClick={handleNext}
              disabled={!isHydrated}
              className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-surface hover:border-primary transition-all duration-200 disabled:opacity-50"
              aria-label="Next testimonial">

              <Icon name="ChevronRightIcon" size={24} className="text-text-primary" />
            </button>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialCarousel;